import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import { fetchExerciseMonthlyReport } from "../../../store/slices/reportSlice";
import { fetchPatients } from "../../../store/slices/patientsSlice";
import Breadcrumbs from "../../../components/common/Breadcrumbs";
import MainLayout from "../../../components/layout/MainLayout";
import usePatientCases from "../../../utils/usePatientCases";

const selectStyles = {
  control: (base) => ({ ...base, minHeight: "36px", fontSize: "0.85rem", borderRadius: "0.375rem", borderColor: "#d9dee3" }),
  menu: (base) => ({ ...base, fontSize: "0.85rem", zIndex: 9999 }),
  option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#666ee8" : state.isFocused ? "#f5f5f9" : "white", color: state.isSelected ? "white" : "#566a7f", cursor: "pointer" }),
};

const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);

// Coloured badge for the overall performance label
const perfBadge = (label) => {
  const map = {
    Excellent: "bg-label-success",
    Good: "bg-label-primary",
    Fair: "bg-label-warning",
    Poor: "bg-label-danger",
    "No Activity": "bg-label-secondary",
  };
  return <span className={`badge ${map[label] || "bg-label-secondary"}`}>{label || "—"}</span>;
};

const ExerciseMonthlyReport = () => {
  const dispatch = useDispatch();
  const { exerciseMonthlyReport: report, isLoading, error } = useSelector((s) => s.reports);
  const { patients } = useSelector((s) => s.patients);

  const [patientId, setPatientId] = useState("");
  const [caseId, setCaseId] = useState("");

  const { caseOptions, ready: casesReady } = usePatientCases(patientId);
  useEffect(() => {
    setCaseId((prev) => {
      const next = caseOptions.length === 1 ? caseOptions[0].value : "";
      return prev === next ? prev : next;
    });
  }, [caseOptions]);
  const needsCase = caseOptions.length > 1 && !caseId;

  useEffect(() => { dispatch(fetchPatients()); }, [dispatch]);
  useEffect(() => {
    // Only the client (and case, if multiple) is selected — the backend auto-detects
    // the latest month with exercise entries and returns it as clientInfo.reporting_month.
    if (!patientId || !casesReady || needsCase) return;
    dispatch(fetchExerciseMonthlyReport({ patientId, caseId: caseId || undefined }));
  }, [dispatch, patientId, caseId, casesReady, needsCase]);

  const patientOptions = (patients || []).map((p) => ({
    value: p.id,
    label: `${p.first_name || ""} ${p.last_name || ""} (${p.mr_no || "N/A"})`.trim(),
  }));

  const ci = report?.clientInfo || {};
  const perf = report?.performance || {};
  const summary = report?.activitySummary || [];
  const weekly = report?.weeklyProgress || [];
  const outcome = report?.outcome || {};

  const fileBase = `Exercise_Monthly_Report_${(ci.client_name || "client").replace(/\s+/g, "_")}_${(ci.reporting_month || "").replace(/\s+/g, "_")}`;

  /* ---------------- EXPORTS ---------------- */
  const exportPDF = () => {
    const doc = new jsPDF("p", "mm", "a4");
    doc.setFontSize(14);
    doc.text("Exercise Monthly Report", 14, 15);
    doc.setFontSize(9);
    doc.text(`Client: ${val(ci.client_name)}   MR No: ${val(ci.mr_no)}   Month: ${val(ci.reporting_month)}`, 14, 22);

    autoTable(doc, {
      startY: 26,
      head: [["Client Information", ""]],
      body: [
        ["Client Name", val(ci.client_name)],
        ["MR No.", val(ci.mr_no)],
        ["Reporting Month", val(ci.reporting_month)],
        ["Ward", val(ci.ward)],
        ["Exercise Goal", val(ci.exercise_goal)],
        ["Exercise Plan", val(ci.exercise_plan)],
        ["Responsible Staff", val(ci.responsible_staff)],
      ],
      theme: "grid", headStyles: { fillColor: [60, 60, 60] }, styles: { fontSize: 8 },
    });

    autoTable(doc, {
      head: [["Monthly Exercise Performance", "Result"]],
      body: [
        ["Total Planned Exercise Days", val(perf.planned_days)],
        ["Total Completed Exercise Days", val(perf.completed_days)],
        ["Overall Compliance (%)", `${val(perf.overall_compliance)}%`],
        ["Average Weekly Participation", val(perf.avg_weekly_participation)],
        ["Overall Performance", val(perf.overall_performance)],
      ],
      theme: "grid", headStyles: { fillColor: [60, 60, 60] }, styles: { fontSize: 8 },
    });

    autoTable(doc, {
      head: [["Exercise", "Planned", "Completed", "Compliance %"]],
      body: summary.map((r) => [r.exercise, r.planned, r.completed, `${r.compliance}%`]),
      theme: "grid", headStyles: { fillColor: [60, 60, 60] }, styles: { fontSize: 8 },
    });

    autoTable(doc, {
      head: [["Week", "Sessions Completed", "Weight", "Weekly Assessment"]],
      body: weekly.map((w) => [w.week, w.sessions_completed, w.weight, w.assessment]),
      theme: "grid", headStyles: { fillColor: [60, 60, 60] }, styles: { fontSize: 8 },
    });

    autoTable(doc, {
      head: [["Rehabilitation Outcome", "Status"]],
      body: [
        ["Physical Progress", val(outcome.physical_progress)],
        ["Goal Achievement", val(outcome.goal_achievement)],
        ["Clinical Recommendation", val(outcome.clinical_recommendation)],
      ],
      theme: "grid", headStyles: { fillColor: [60, 60, 60] }, styles: { fontSize: 8 },
    });

    doc.save(`${fileBase}.pdf`);
  };

  const exportDocx = () => {
    const t = (rows) => rows.map((r) => `<tr>${r.map((c, i) => `<td style="border:1px solid #444;padding:5px;${i === 0 ? "font-weight:bold;background:#eef;" : ""}">${c}</td>`).join("")}</tr>`).join("");
    const html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'><head><meta charset='utf-8'></head><body>
      <h2 style="text-align:center">Exercise Monthly Report</h2>
      <h4>1. Client Information</h4>
      <table style="border-collapse:collapse;width:100%">${t([
      ["Client Name", val(ci.client_name)], ["MR No.", val(ci.mr_no)], ["Reporting Month", val(ci.reporting_month)],
      ["Ward", val(ci.ward)], ["Exercise Goal", val(ci.exercise_goal)], ["Exercise Plan", val(ci.exercise_plan)], ["Responsible Staff", val(ci.responsible_staff)],
    ])}</table>
      <h4>2. Monthly Exercise Performance</h4>
      <table style="border-collapse:collapse;width:100%">${t([
      ["Total Planned Exercise Days", val(perf.planned_days)], ["Total Completed Exercise Days", val(perf.completed_days)],
      ["Overall Compliance (%)", `${val(perf.overall_compliance)}%`], ["Average Weekly Participation", val(perf.avg_weekly_participation)], ["Overall Performance", val(perf.overall_performance)],
    ])}</table>
      <h4>3. Exercise Activity Summary</h4>
      <table style="border-collapse:collapse;width:100%"><tr><th style="border:1px solid #444;padding:5px">Exercise</th><th style="border:1px solid #444;padding:5px">Planned</th><th style="border:1px solid #444;padding:5px">Completed</th><th style="border:1px solid #444;padding:5px">Compliance %</th></tr>
      ${summary.map((r) => `<tr><td style="border:1px solid #444;padding:5px">${r.exercise}</td><td style="border:1px solid #444;padding:5px">${r.planned}</td><td style="border:1px solid #444;padding:5px">${r.completed}</td><td style="border:1px solid #444;padding:5px">${r.compliance}%</td></tr>`).join("")}</table>
      <h4>4. Weekly Progress</h4>
      <table style="border-collapse:collapse;width:100%"><tr><th style="border:1px solid #444;padding:5px">Week</th><th style="border:1px solid #444;padding:5px">Sessions Completed</th><th style="border:1px solid #444;padding:5px">Weight</th><th style="border:1px solid #444;padding:5px">Weekly Assessment</th></tr>
      ${weekly.map((w) => `<tr><td style="border:1px solid #444;padding:5px">${w.week}</td><td style="border:1px solid #444;padding:5px">${w.sessions_completed}</td><td style="border:1px solid #444;padding:5px">${w.weight}</td><td style="border:1px solid #444;padding:5px">${w.assessment}</td></tr>`).join("")}</table>
      <h4>5. Rehabilitation Outcome</h4>
      <table style="border-collapse:collapse;width:100%">${t([
      ["Physical Progress", val(outcome.physical_progress)], ["Goal Achievement", val(outcome.goal_achievement)], ["Clinical Recommendation", val(outcome.clinical_recommendation)],
    ])}</table>
      </body></html>`;
    saveAs(new Blob(["﻿", html], { type: "application/msword" }), `${fileBase}.doc`);
  };

  const canExport = report && report.hasData;

  return (
    <MainLayout>
      <Breadcrumbs />

      {/* Header + filters */}
      <div className="card shadow-sm mb-3">
        <div className="card-body" style={{ background: "linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)" }}>
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h4 className="mb-1 text-dark fw-bold">Exercise Monthly Report</h4>
              <div className="text-muted small">Auto-generated from the daily Exercise Schedule (EMR) entries</div>
            </div>
            <div className="d-flex gap-2 flex-wrap align-items-end">
              <div style={{ minWidth: "240px" }}>
                <label className="form-label small fw-bold mb-1">Select Client</label>
                <Select
                  options={patientOptions}
                  styles={selectStyles}
                  value={patientOptions.find((o) => o.value === patientId) || null}
                  onChange={(o) => setPatientId(o ? o.value : "")}
                  placeholder="Search client..."
                  isSearchable isClearable
                />
              </div>
              {caseOptions.length > 1 && (
                <div style={{ minWidth: "240px" }}>
                  <label className="form-label small fw-bold mb-1">Select Case</label>
                  <Select
                    options={caseOptions}
                    styles={selectStyles}
                    value={caseOptions.find((o) => o.value === caseId) || null}
                    onChange={(o) => setCaseId(o ? o.value : "")}
                    placeholder="Select case..."
                    isSearchable
                  />
                </div>
              )}
              <div>
                <label className="form-label small fw-bold mb-1">Reporting Month</label>
                <input
                  type="text"
                  className="form-control form-control-sm bg-white"
                  value={report && report.hasData ? (ci.reporting_month || "") : ""}
                  placeholder="Auto-detected"
                  readOnly
                  style={{ minHeight: "36px", minWidth: "160px" }}
                />
              </div>
            </div>
          </div>
          {canExport && (
            <div className="mt-3 border-top pt-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
              <style jsx="true" global="true">{`
                @media print {
                  body * {
                    visibility: hidden !important;
                  }
                  #exercise-report-print,
                  #exercise-report-print * {
                    visibility: visible !important;
                  }
                  #exercise-report-print {
                    position: absolute !important;
                    left: 0 !important;
                    top: 0 !important;
                    width: 100% !important;
                    margin: 0 !important;
                    padding: 0 !important;
                  }
                  .layout-navbar,
                  .layout-menu,
                  .btn,
                  .card-header button,
                  .breadcrumbs,
                  .card-header div,
                  .footer {
                    display: none !important;
                  }
                }
              `}</style>
              <div className="d-flex align-items-center gap-2">
                <div className="dropdown">
                  <button className="btn btn-label-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <i className="ri-download-2-line me-1"></i> Export
                  </button>
                  <ul className="dropdown-menu shadow-sm">
                    <li>
                      <button className="dropdown-item" onClick={exportPDF}>
                        <i className="ri-file-pdf-line me-1 text-danger"></i> PDF
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={exportDocx}>
                        <i className="ri-file-word-line me-1 text-primary"></i> Word
                      </button>
                    </li>
                    <li>
                      <button className="dropdown-item" onClick={() => window.print()}>
                        <i className="ri-printer-line me-1 text-secondary"></i> Print
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {!patientId ? (
        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
          <i className="ri-run-line fs-1 d-block mb-2"></i>
          Please select a client to generate the Exercise Monthly Report.
        </div></div>
      ) : needsCase ? (
        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
          <i className="ri-folder-user-line fs-1 d-block mb-2"></i>
          This client has multiple cases. Please select a case to generate the report.
        </div></div>
      ) : isLoading ? (
        <div className="card shadow-sm"><div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        </div></div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : report && !report.hasData ? (
        <div className="card shadow-sm"><div className="card-body text-center text-muted py-5">
          <i className="ri-information-line fs-1 d-block mb-2"></i>
          No Exercise Schedule entries found for this client.
        </div></div>
      ) : report ? (
        <div id="exercise-report-print">
          {/* 1. Client Information */}
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-label-primary py-2"><h6 className="mb-0 fw-bold">1. Client Information</h6></div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <tbody>
                  <tr><th style={{ width: "30%" }} className="bg-light">Client Name</th><td>{val(ci.client_name)}</td></tr>
                  <tr><th className="bg-light">MR No.</th><td>{val(ci.mr_no)}</td></tr>
                  <tr><th className="bg-light">Reporting Month</th><td>{val(ci.reporting_month)}</td></tr>
                  <tr><th className="bg-light">Ward</th><td>{val(ci.ward)}</td></tr>
                  <tr><th className="bg-light">Exercise Goal</th><td>{val(ci.exercise_goal)}</td></tr>
                  <tr><th className="bg-light">Exercise Plan</th><td>{val(ci.exercise_plan)}</td></tr>
                  <tr><th className="bg-light">Responsible Staff</th><td>{val(ci.responsible_staff)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 2. Monthly Exercise Performance */}
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-label-primary py-2"><h6 className="mb-0 fw-bold">2. Monthly Exercise Performance</h6></div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <tbody>
                  <tr><th style={{ width: "40%" }} className="bg-light">Indicator</th><th>Result</th></tr>
                  <tr><td>Total Planned Exercise Days</td><td>{val(perf.planned_days)}</td></tr>
                  <tr><td>Total Completed Exercise Days</td><td>{val(perf.completed_days)}</td></tr>
                  <tr><td>Overall Compliance (%)</td><td><span className="fw-bold">{val(perf.overall_compliance)}%</span></td></tr>
                  <tr><td>Average Weekly Participation</td><td>{val(perf.avg_weekly_participation)}</td></tr>
                  <tr><td>Overall Performance</td><td>{perfBadge(perf.overall_performance)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* 3. Exercise Activity Summary */}
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-label-primary py-2 d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold">3. Exercise Activity Summary</h6>
              <span className="small text-muted">Most: <strong>{val(perf.most_performed)}</strong> · Least: <strong>{val(perf.least_performed)}</strong></span>
            </div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <thead className="table-light">
                  <tr><th>Exercise</th><th className="text-center">Planned</th><th className="text-center">Completed</th><th className="text-center">Compliance %</th></tr>
                </thead>
                <tbody>
                  {summary.map((r) => (
                    <tr key={r.exercise}>
                      <td>{r.exercise}</td>
                      <td className="text-center">{r.planned}</td>
                      <td className="text-center">{r.completed}</td>
                      <td className="text-center">
                        <span className={`badge ${r.compliance >= 80 ? "bg-label-success" : r.compliance >= 50 ? "bg-label-warning" : "bg-label-danger"}`}>{r.compliance}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 4. Weekly Progress */}
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-label-primary py-2"><h6 className="mb-0 fw-bold">4. Weekly Progress</h6></div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <thead className="table-light">
                  <tr><th>Week</th><th className="text-center">Sessions Completed</th><th className="text-center">Weight</th><th>Weekly Assessment</th></tr>
                </thead>
                <tbody>
                  {weekly.map((w) => (
                    <tr key={w.week}>
                      <td className="fw-semibold">{w.week}</td>
                      <td className="text-center">{w.sessions_completed}</td>
                      <td className="text-center">{w.weight}</td>
                      <td>{w.assessment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 5. Rehabilitation Outcome */}
          <div className="card shadow-sm mb-3">
            <div className="card-header bg-label-primary py-2"><h6 className="mb-0 fw-bold">5. Rehabilitation Outcome</h6></div>
            <div className="card-body p-0">
              <table className="table table-bordered mb-0">
                <tbody>
                  <tr><th style={{ width: "30%" }} className="bg-light">Physical Progress</th><td>{val(outcome.physical_progress)}</td></tr>
                  <tr><th className="bg-light">Goal Achievement</th><td>{val(outcome.goal_achievement)}</td></tr>
                  <tr><th className="bg-light">Clinical Recommendation</th><td>{val(outcome.clinical_recommendation)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}
    </MainLayout>
  );
};

export default ExerciseMonthlyReport;
